/* eslint-disable react/no-unknown-property */
import { Box, SxProps } from "@outschool/backpack";
import React from "react";

type TocHeading = {
  heading: string;
  id: string;
};

type ScrollTocProps = {
  /** A list of headings and their corresponding ids. */
  headings: TocHeading[];
  /** A label that appears at the top of the ToC */
  label?: string;
  /** Styles for the parent div */
  sx?: SxProps;
  /** Styles for the heading element */
  headingSx?: SxProps;
  /** Styles for the heading element when the corresponding heading is in the viewport. */
  activeSx?: SxProps;
  /** Whether to enable smoothly scrolling to headings */
  smoothScroll?: boolean;
};

/**
 * A table of contents that styles its headings as active when the corresponding element is at the top of the viewport.
 * Should usually be paired with a sticky implementation with CSS `fixed` or `sticky` positioning.
 * Can be integrated with the `Accordion` component to detect headings on the page.
 */
export default function ScrollToc({
  label,
  headings,
  headingSx = {},
  activeSx = { fontWeight: "bold" },
  smoothScroll = false,
  sx
}: ScrollTocProps) {
  const [activeHeadingId, setActiveHeadingId] = React.useState<null | string>(
    null
  );

  // Activate intersection observer
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      entries =>
        setActiveHeadingId(
          entries.find(entry => entry.isIntersecting)?.target?.id ?? null
        ),
      {
        // Check for intersections at a horizontal line 15% down the page.
        rootMargin: "-25% 0px -75% 0px"
      }
    );
    headings.forEach(heading => {
      const element = document.getElementById(heading.id);
      if (element) {
        observer.observe(element);
      }
    });
    return () => observer?.disconnect();
  }, [headings]);

  return (
    <Box sx={sx}>
      {smoothScroll && (
        <style>{`
          html {
            scroll-behavior: smooth;
          }
        `}</style>
      )}
      <nav>
        <Box
          sx={{
            marginBottom: "10px",
            paddingX: "10px",
            fontWeight: "fontWeightBold"
          }}
        >
          {label}
        </Box>
        <ol
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0
          }}
        >
          {headings.map(({ heading, id }) => {
            return (
              <li key={id}>
                <Box
                  sx={[
                    ...(Array.isArray(headingSx) ? headingSx : [headingSx]),
                    ...(id === activeHeadingId
                      ? Array.isArray(activeSx)
                        ? activeSx
                        : [activeSx]
                      : [])
                  ]}
                >
                  <a
                    style={{ color: "initial", textDecoration: "none" }}
                    href={`#${id}`}
                  >
                    {heading}
                  </a>
                </Box>
              </li>
            );
          })}
        </ol>
      </nav>
    </Box>
  );
}
